/**
 * MFAVerificationModal Component
 *
 * Modal wrapper for MFA verification during login
 * Shows after password login when user has MFA enabled
 */

'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useMFA } from '@/hooks/useMFA';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Shield, AlertCircle, Loader2, Key } from 'lucide-react';

export default function MFAVerificationModal({ tempToken, onSuccess, onCancel }) {
  const router = useRouter();
  const { verifyMFA, isLoading, error } = useMFA();

  const [code, setCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);

  const handleVerify = async (e) => {
    if (e) e.preventDefault();

    if (!code) {
      return;
    }

    try {
      const result = await verifyMFA(tempToken, code);

      if (result?.success) {
        // Store the real token from MFA verification
        if (result.token) {
          localStorage.setItem('xoffi_token', result.token);
        }
        if (result.user) {
          localStorage.setItem('xoffi_user', JSON.stringify(result.user));
        }

        // Call success callback
        if (onSuccess) {
          onSuccess(result);
        } else {
          // Default: redirect to dashboard
          router.push('/dashboard');
        }
      }
    } catch (err) {
      console.error('MFA verification failed:', err);
    }
  };

  const handleCodeChange = (value) => {
    if (useBackupCode) {
      // Backup code: 8 characters alphanumeric
      setCode(value.toUpperCase().slice(0, 8));
    } else {
      // TOTP code: 6 digits
      setCode(value.replace(/\D/g, '').slice(0, 6));
    }
  };

  return (
    <Dialog open={true} onOpenChange={() => onCancel && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 rounded-full">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
            <DialogTitle>Zwei-Faktor-Authentifizierung</DialogTitle>
          </div>
        </DialogHeader>

        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground mb-4">
              {useBackupCode
                ? 'Gib einen deiner 8-stelligen Backup-Codes ein'
                : 'Gib den 6-stelligen Code aus deiner Authenticator-App ein'}
            </p>

            <Input
              type="text"
              maxLength={useBackupCode ? 8 : 6}
              placeholder={useBackupCode ? 'ABCD1234' : '000000'}
              value={code}
              onChange={(e) => handleCodeChange(e.target.value)}
              className={`text-center text-2xl tracking-widest font-mono ${
                useBackupCode ? '' : 'uppercase'
              }`}
              autoFocus
              disabled={isLoading}
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="flex gap-2">
            {onCancel && (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isLoading}
                className="flex-1"
              >
                Abbrechen
              </Button>
            )}
            <Button
              type="submit"
              onClick={handleVerify}
              disabled={code.length < (useBackupCode ? 8 : 6) || isLoading}
              className="flex-1"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifiziere...
                </>
              ) : (
                'Verifizieren'
              )}
            </Button>
          </div>

          {/* Toggle Backup Code */}
          <div className="text-center">
            <button
              type="button"
              onClick={() => {
                setUseBackupCode(!useBackupCode);
                setCode('');
              }}
              disabled={isLoading}
              className="text-xs text-muted-foreground hover:text-primary hover:underline transition-colors disabled:opacity-50"
            >
              {useBackupCode ? (
                <>
                  <Key className="inline w-3 h-3 mr-1" />
                  Authenticator-Code verwenden
                </>
              ) : (
                <>
                  <Key className="inline w-3 h-3 mr-1" />
                  Backup-Code verwenden
                </>
              )}
            </button>
          </div>
        </form>

        {/* Info Text */}
        <p className="text-xs text-center text-muted-foreground mt-4">
          Gerät verloren?{' '}
          <a href="/support" className="text-primary hover:underline">
            Support kontaktieren
          </a>
        </p>
      </DialogContent>
    </Dialog>
  );
}
